import { computed, type Ref } from 'vue'
import type { MultiselectOption } from '@ironsight/types'
import {
  getSelectAllExcludedScopes,
  getSelectAllExcludedSearchTexts,
  getSelectAllExclusions,
  getSelectAllInclusions,
  getSelectAllScopes,
  getSelectAllSearchText,
  isSelectAllValue,
  makeSelectAllValue,
  SELECT_ALL_SENTINEL,
} from '@/components/jobs/job-search-page/useFilterChips'
import type { SelectAllScope } from '@/components/jobs/job-search-page/useFilterChips'

export type SearchSelectAllMode = 'sentinel' | 'visible-options'
export type NormalDeselectAllMode = 'clear-all' | 'visible-items'

type SelectAllKey = string | number | null

/**
 * Adapter contract for field-specific item shapes. The engine only understands
 * keys, visible items, sentinel options, and optional location-like scopes.
 */
type UseSelectAllSentinelArgs<TItem, TKey extends SelectAllKey> = {
  value: () => unknown
  searchText: Ref<string>
  selectedValues: Ref<TKey[]>
  visibleItems: () => TItem[]
  getItemValue: (item: TItem) => TKey
  itemToOption: (item: TItem) => MultiselectOption
  resolveOption: (value: TKey) => MultiselectOption
  normalValueToKey: (value: unknown) => TKey | null | undefined
  emitSelectAllValue: (value: MultiselectOption[]) => void
  emitNormalValue: (value: TKey[]) => void
  itemMatchesSearchText: (item: TItem | MultiselectOption, searchText: string) => boolean
  searchSelectAllMode: SearchSelectAllMode
  normalDeselectAllMode?: NormalDeselectAllMode
  requireSearchScopeCoverage?: boolean
  /** Scope callbacks are optional so simple multiselects can stay search-only. */
  getCurrentScope?: () => SelectAllScope | null
  itemMatchesScope?: (item: TItem | MultiselectOption, scope: SelectAllScope) => boolean
  scopesMatch?: (a: SelectAllScope | null, b: SelectAllScope | null) => boolean
  scopeContains?: (parentScope: SelectAllScope, childScope: SelectAllScope) => boolean
}

/**
 * Compares two select-all scopes by their identity fields.
 */
function defaultScopesMatch(a: SelectAllScope | null, b: SelectAllScope | null) {
  return (
    (a?.locationTypeId ?? null) === (b?.locationTypeId ?? null) &&
    (a?.parentLocationId ?? null) === (b?.parentLocationId ?? null) &&
    (a?.searchText?.toLowerCase() ?? '') === (b?.searchText?.toLowerCase() ?? '')
  )
}

/**
 * Determines whether a selected parent scope fully covers a child scope.
 */
function defaultScopeContains(parentScope: SelectAllScope, childScope: SelectAllScope) {
  if (
    typeof parentScope.locationTypeId === 'number' &&
    parentScope.locationTypeId !== childScope.locationTypeId
  ) {
    return false
  }

  if (
    typeof parentScope.parentLocationId === 'number' &&
    parentScope.parentLocationId !== childScope.parentLocationId &&
    !(childScope.parentLocationAncestorIds?.includes(parentScope.parentLocationId) ?? false)
  ) {
    return false
  }

  if (
    parentScope.searchText &&
    parentScope.searchText.toLowerCase() !== childScope.searchText?.toLowerCase()
  ) {
    return false
  }

  return true
}

/**
 * Compares search text case-insensitively after trimming whitespace.
 */
export function searchTextsMatch(a: string | null | undefined, b: string | null | undefined) {
  return (a?.trim().toLowerCase() ?? '') === (b?.trim().toLowerCase() ?? '')
}

/**
 * Shared engine for the SELECT_ALL sentinel state machine.
 *
 * Components adapt their local item/value models through callbacks rather than
 * duplicating exclusion, inclusion, search-scope, and location-scope transitions.
 * This supports both simple multiselect fields and scope-aware location filters.
 */
export function useSelectAllSentinel<TItem, TKey extends SelectAllKey>({
  value,
  searchText,
  selectedValues,
  visibleItems,
  getItemValue,
  itemToOption,
  resolveOption,
  normalValueToKey,
  emitSelectAllValue,
  emitNormalValue,
  itemMatchesSearchText,
  searchSelectAllMode,
  normalDeselectAllMode = 'clear-all',
  requireSearchScopeCoverage = true,
  getCurrentScope,
  itemMatchesScope,
  scopesMatch = defaultScopesMatch,
  scopeContains = defaultScopeContains,
}: UseSelectAllSentinelArgs<TItem, TKey>) {
  const isSelectAllMode = computed(() => isSelectAllValue(value()))
  const activeSelectAllSearchText = computed(() => getSelectAllSearchText(value()))
  const activeSelectAllScopes = computed(() => getSelectAllScopes(value()))
  const activeSelectAllExcludedScopes = computed(() => getSelectAllExcludedScopes(value()))

  const excludedValues = computed<Set<TKey>>(() => {
    if (!isSelectAllMode.value) return new Set()
    return new Set(getSelectAllExclusions(value()).map((option) => option.value as TKey))
  })

  const includedValues = computed<Set<TKey>>(() => {
    if (!isSelectAllMode.value) return new Set()
    return new Set(getSelectAllInclusions(value()).map((option) => option.value as TKey))
  })

  const excludedSearchTexts = computed<string[]>(() => {
    if (!isSelectAllMode.value) return []
    return getSelectAllExcludedSearchTexts(value())
  })

  /**
   * Checks whether an exact scope already exists in a scope list.
   */
  function hasScope(scopes: SelectAllScope[], targetScope: SelectAllScope | null) {
    return targetScope !== null && scopes.some((scope) => scopesMatch(scope, targetScope))
  }

  /**
   * Adds a scope only when it is present and not already tracked.
   */
  function addScope(scopes: SelectAllScope[], targetScope: SelectAllScope | null) {
    if (!targetScope || hasScope(scopes, targetScope)) return scopes
    return [...scopes, targetScope]
  }

  /**
   * Removes an exact scope from a scope list.
   */
  function removeScope(scopes: SelectAllScope[], targetScope: SelectAllScope | null) {
    if (!targetScope) return scopes
    return scopes.filter((scope) => !scopesMatch(scope, targetScope))
  }

  /**
   * Removes child scopes that are already covered by the provided parent scope.
   */
  function removeContainedScopes(scopes: SelectAllScope[], targetScope: SelectAllScope | null) {
    if (!targetScope) return scopes
    return scopes.filter((scope) => !scopeContains(targetScope, scope))
  }

  /**
   * Checks whether an item matches the active searchText sentinel scope.
   */
  function itemMatchesSelectAllSearchScope(item: TItem | MultiselectOption): boolean {
    const selectedSearchText = activeSelectAllSearchText.value
    if (!selectedSearchText) return true

    return itemMatchesSearchText(item, selectedSearchText)
  }

  /**
   * Checks whether an item is covered by the active select-all universe.
   */
  function itemMatchesSelectAllScope(item: TItem | MultiselectOption): boolean {
    // Explicit scopes take precedence over a plain searchText sentinel. This is
    // what lets Location filters represent "all wells under parent X".
    if (activeSelectAllScopes.value.length > 0 && itemMatchesScope) {
      return activeSelectAllScopes.value.some((scope) => itemMatchesScope(item, scope))
    }

    return itemMatchesSelectAllSearchScope(item)
  }

  /**
   * Checks whether an item is excluded by active excluded scopes or search text exclusions.
   */
  function itemMatchesExcludedScope(item: TItem | MultiselectOption): boolean {
    return (
      (itemMatchesScope &&
        activeSelectAllExcludedScopes.value.some((scope) => itemMatchesScope(item, scope))) ||
      excludedSearchTexts.value.some((searchText) => itemMatchesSearchText(item, searchText))
    )
  }

  /**
   * Checks whether an option or item matches the currently typed search text.
   */
  function itemMatchesCurrentSearchText(item: TItem | MultiselectOption) {
    const normalizedSearchText = searchText.value.trim()
    return !normalizedSearchText || itemMatchesSearchText(item, normalizedSearchText)
  }

  /**
   * Determines whether the current UI search is covered by the active sentinel search scope.
   */
  function currentSearchIsCoveredBySelectAllScope() {
    const normalizedSearchText = searchText.value.trim()
    const selectedSearchText = activeSelectAllSearchText.value

    if (!normalizedSearchText) return !selectedSearchText
    return !selectedSearchText || searchTextsMatch(selectedSearchText, normalizedSearchText)
  }

  /**
   * Checks for explicit exclusions that apply to the current UI search.
   */
  function hasExcludedItemsInCurrentSearchScope() {
    return getSelectAllExclusions(value()).some(itemMatchesCurrentSearchText)
  }

  /**
   * Checks for excluded search scopes that apply to the current UI search.
   */
  function hasExcludedSearchTextInCurrentSearchScope() {
    const normalizedSearchText = searchText.value.trim()
    if (!normalizedSearchText) return excludedSearchTexts.value.length > 0

    return excludedSearchTexts.value.some((excludedSearchText) =>
      searchTextsMatch(excludedSearchText, normalizedSearchText),
    )
  }

  /**
   * Computes the checkbox state for a visible item in normal or select-all mode.
   */
  function isItemSelected(item: TItem): boolean {
    const itemValue = getItemValue(item)

    if (isSelectAllMode.value) {
      if (includedValues.value.has(itemValue)) return true

      return (
        itemMatchesSelectAllScope(item) &&
        !excludedValues.value.has(itemValue) &&
        !itemMatchesExcludedScope(item)
      )
    }

    return selectedValues.value.includes(itemValue)
  }

  /**
   * Resolves normal selected keys back into display options.
   */
  function getOptionsFromSelectedValues(): MultiselectOption[] {
    return selectedValues.value.map((selectedValue) => resolveOption(selectedValue))
  }

  /**
   * Converts an incoming non-sentinel value array into the engine's key array.
   */
  function mapSelectedValues(nextValue: unknown): TKey[] {
    if (!Array.isArray(nextValue) || isSelectAllValue(nextValue)) {
      return []
    }

    return nextValue
      .map(normalValueToKey)
      .filter(
        (itemValue): itemValue is TKey =>
          itemValue !== undefined && itemValue !== SELECT_ALL_SENTINEL,
      )
  }

  /**
   * Compares selected key arrays while preserving order-sensitive UI behavior.
   */
  function selectedValuesChanged(nextValues: TKey[]) {
    return (
      nextValues.length !== selectedValues.value.length ||
      nextValues.some((itemValue, index) => itemValue !== selectedValues.value[index])
    )
  }

  /**
   * Emits the current normal-mode selected keys through the adapter.
   */
  function emitSelectedOptions() {
    emitNormalValue([...selectedValues.value])
  }

  /**
   * Builds and emits a sentinel value while enforcing scope/searchText exclusivity.
   */
  function emitSelectAll(
    exclusions: MultiselectOption[] = [],
    nextSearchText?: string | null,
    inclusions: MultiselectOption[] = [],
    excludedSearchTexts: string[] = [],
    scopes: SelectAllScope[] = [],
    excludedScopes: SelectAllScope[] = [],
  ) {
    // A scoped sentinel stores scopes instead of a top-level searchText so the
    // backend can distinguish hierarchy filters from plain text searches.
    emitSelectAllValue(
      makeSelectAllValue(
        exclusions,
        scopes.length > 0 ? null : nextSearchText,
        inclusions,
        excludedSearchTexts,
        scopes,
        excludedScopes,
      ),
    )
  }

  /**
   * Adds an item as an explicit inclusion inside select-all mode.
   */
  function includeItem(item: TItem) {
    const itemValue = getItemValue(item)
    const currentExclusions = getSelectAllExclusions(value())
    const currentInclusions = getSelectAllInclusions(value())
    const currentExcludedSearchTexts = getSelectAllExcludedSearchTexts(value())
    const currentScopes = getSelectAllScopes(value())
    const currentExcludedScopes = getSelectAllExcludedScopes(value())
    const alreadyIncluded = currentInclusions.some((option) => option.value === itemValue)
    const newInclusions = alreadyIncluded
      ? currentInclusions
      : [...currentInclusions, itemToOption(item)]
    const newExclusions = currentExclusions.filter((option) => option.value !== itemValue)

    emitSelectAll(
      newExclusions,
      activeSelectAllSearchText.value,
      newInclusions,
      currentExcludedSearchTexts,
      currentScopes,
      currentExcludedScopes,
    )
  }

  /**
   * Toggles one item in normal mode or in the select-all inclusion/exclusion model.
   */
  function toggleItem(item: TItem) {
    const itemValue = getItemValue(item)

    if (isSelectAllMode.value) {
      const currentExclusions = getSelectAllExclusions(value())
      const currentInclusions = getSelectAllInclusions(value())
      const currentExcludedSearchTexts = getSelectAllExcludedSearchTexts(value())
      const currentScopes = getSelectAllScopes(value())
      const currentExcludedScopes = getSelectAllExcludedScopes(value())
      const selectAllSearchText = currentScopes.length > 0 ? null : activeSelectAllSearchText.value

      // Items outside the selected universe cannot be represented as exclusions,
      // so toggling them creates/removes explicit inclusions instead.
      if (!itemMatchesSelectAllScope(item) || itemMatchesExcludedScope(item)) {
        const alreadyIncluded = currentInclusions.some((option) => option.value === itemValue)
        const newInclusions = alreadyIncluded
          ? currentInclusions.filter((option) => option.value !== itemValue)
          : [...currentInclusions, itemToOption(item)]
        const newExclusions = currentExclusions.filter((option) => option.value !== itemValue)

        emitSelectAll(
          newExclusions,
          selectAllSearchText,
          newInclusions,
          currentExcludedSearchTexts,
          currentScopes,
          currentExcludedScopes,
        )
        return
      }

      // In-scope selected items are toggled by exclusions: all except this item.
      const alreadyExcluded = currentExclusions.some((option) => option.value === itemValue)
      const newExclusions = alreadyExcluded
        ? currentExclusions.filter((option) => option.value !== itemValue)
        : [...currentExclusions, itemToOption(item)]
      const newInclusions = currentInclusions.filter((option) => option.value !== itemValue)

      emitSelectAll(
        newExclusions,
        selectAllSearchText,
        newInclusions,
        currentExcludedSearchTexts,
        currentScopes,
        currentExcludedScopes,
      )
      return
    }

    const index = selectedValues.value.indexOf(itemValue)
    if (index > -1) {
      selectedValues.value.splice(index, 1)
    } else {
      selectedValues.value.push(itemValue)
    }
    emitSelectedOptions()
  }

  /**
   * Selects currently visible search results without creating a backend search sentinel.
   */
  function selectAllVisibleOptions(search: string) {
    // Non-paginated search select-all means "the visible filtered set", not all
    // backend matches. Paginated fields use the sentinel path instead.
    if (!isSelectAllMode.value) {
      const selectedSet = new Set(selectedValues.value)
      const nextSelectedValues = [...selectedValues.value]

      for (const item of visibleItems()) {
        const itemValue = getItemValue(item)
        if (!selectedSet.has(itemValue)) {
          selectedSet.add(itemValue)
          nextSelectedValues.push(itemValue)
        }
      }

      selectedValues.value = nextSelectedValues
      emitSelectedOptions()
      return
    }

    if (searchTextsMatch(activeSelectAllSearchText.value, search)) {
      const visibleValues = new Set(visibleItems().map((item) => getItemValue(item)))
      const currentExcludedSearchTexts = getSelectAllExcludedSearchTexts(value())
      const newExclusions = getSelectAllExclusions(value()).filter(
        (option) =>
          !visibleValues.has(option.value as TKey) && !itemMatchesSearchText(option, search),
      )

      emitSelectAll(
        newExclusions,
        activeSelectAllSearchText.value,
        getSelectAllInclusions(value()),
        currentExcludedSearchTexts,
      )
      return
    }

    if (!activeSelectAllSearchText.value) {
      selectAllSearchWithinGlobalSelectAll(search)
      return
    }

    const visibleValues = new Set(visibleItems().map((item) => getItemValue(item)))
    const currentInclusions = getSelectAllInclusions(value())
    const includedValues = new Set<SelectAllKey>(
      currentInclusions.map((option) => option.value as SelectAllKey),
    )
    const newInclusions = [...currentInclusions]

    for (const item of visibleItems()) {
      const itemValue = getItemValue(item)
      if (!includedValues.has(itemValue)) {
        includedValues.add(itemValue)
        newInclusions.push(itemToOption(item))
      }
    }

    const newExclusions = getSelectAllExclusions(value()).filter(
      (option) => !visibleValues.has(option.value as TKey),
    )

    emitSelectAll(
      newExclusions,
      activeSelectAllSearchText.value,
      newInclusions,
      getSelectAllExcludedSearchTexts(value()),
    )
  }

  /**
   * Re-includes a searched subset while currently in global select-all mode.
   */
  function selectAllSearchWithinGlobalSelectAll(search: string) {
    const visibleValues = new Set(visibleItems().map((item) => getItemValue(item)))
    const currentExcludedSearchTexts = getSelectAllExcludedSearchTexts(value())
    const remainingExcludedSearchTexts = currentExcludedSearchTexts.filter(
      (excludedSearchText) => !searchTextsMatch(excludedSearchText, search),
    )
    const newExclusions = getSelectAllExclusions(value()).filter(
      (option) =>
        !visibleValues.has(option.value as TKey) && !itemMatchesSearchText(option, search),
    )
    const newInclusions = getSelectAllInclusions(value()).filter(
      (option) => !visibleValues.has(option.value as TKey),
    )

    emitSelectAll(newExclusions, null, newInclusions, remainingExcludedSearchTexts)
  }

  /**
   * Selects or re-includes the current scope for scope-aware fields.
   */
  function selectAllCurrentScope(currentScope: SelectAllScope) {
    const visibleValues = new Set(visibleItems().map((item) => getItemValue(item)))
    const currentScopes = getSelectAllScopes(value())
    const currentExcludedScopes = getSelectAllExcludedScopes(value())
    const currentExcludedSearchTexts = getSelectAllExcludedSearchTexts(value())
    const isGlobalSelectAll = currentScopes.length === 0 && !activeSelectAllSearchText.value
    // Global select-all already includes the scope; scoped select-all only adds
    // a scope when we are not already selecting everything.
    const newScopes = isGlobalSelectAll ? currentScopes : addScope(currentScopes, currentScope)
    const newExcludedScopes = removeContainedScopes(currentExcludedScopes, currentScope)
    const newExcludedSearchTexts = currentScope.searchText
      ? currentExcludedSearchTexts.filter(
          (text) => !searchTextsMatch(text, currentScope.searchText),
        )
      : currentExcludedSearchTexts
    const newExclusions = getSelectAllExclusions(value()).filter(
      (option) =>
        !visibleValues.has(option.value as TKey) &&
        !(itemMatchesScope?.(option, currentScope) ?? false),
    )
    const newInclusions = getSelectAllInclusions(value()).filter(
      (option) =>
        !visibleValues.has(option.value as TKey) &&
        !(itemMatchesScope?.(option, currentScope) ?? false),
    )

    emitSelectAll(
      newExclusions,
      newScopes.length > 0 ? null : activeSelectAllSearchText.value,
      newInclusions,
      newExcludedSearchTexts,
      newScopes,
      newExcludedScopes,
    )
  }

  /**
   * Handles the public Select All action for normal, searched, and scoped states.
   */
  function selectAllFiltered() {
    const currentScope = getCurrentScope?.() ?? null
    const normalizedSearchText = searchText.value.trim()

    // Scope-aware fields, currently locations, prefer scope sentinels over
    // searchText sentinels whenever the UI has an active scope.
    if (!isSelectAllMode.value && currentScope) {
      emitSelectAll([], null, [], [], [currentScope])
      return
    }

    if (isSelectAllMode.value && currentScope) {
      selectAllCurrentScope(currentScope)
      return
    }

    if (normalizedSearchText) {
      if (searchSelectAllMode === 'visible-options') {
        selectAllVisibleOptions(normalizedSearchText)
        return
      }

      if (
        isSelectAllMode.value &&
        searchTextsMatch(activeSelectAllSearchText.value, normalizedSearchText)
      ) {
        const visibleValues = new Set(visibleItems().map((item) => getItemValue(item)))
        const currentExcludedSearchTexts = getSelectAllExcludedSearchTexts(value())
        const newExclusions = getSelectAllExclusions(value()).filter(
          (option) =>
            !visibleValues.has(option.value as TKey) &&
            !itemMatchesSearchText(option, normalizedSearchText),
        )
        emitSelectAll(
          newExclusions,
          normalizedSearchText,
          getSelectAllInclusions(value()),
          currentExcludedSearchTexts,
        )
      } else if (isSelectAllMode.value && !activeSelectAllSearchText.value) {
        selectAllSearchWithinGlobalSelectAll(normalizedSearchText)
      } else {
        emitSelectAll([], normalizedSearchText)
      }
      return
    }

    emitSelectAll()
  }

  /**
   * Deselects or excludes the current scope for scope-aware fields.
   */
  function deselectAllCurrentScope(currentScope: SelectAllScope) {
    const currentScopes = getSelectAllScopes(value())
    const currentExcludedScopes = getSelectAllExcludedScopes(value())
    const currentExcludedSearchTexts = getSelectAllExcludedSearchTexts(value())
    const exactScopeSelected = hasScope(currentScopes, currentScope)
    const isGlobalSelectAll = currentScopes.length === 0 && !activeSelectAllSearchText.value
    const newScopes = removeScope(currentScopes, currentScope)
    // Deselecting an explicitly selected scope removes it; deselecting a subset
    // of a broader/global selection records that subset as an excluded scope.
    const shouldExcludeScope = !exactScopeSelected
    const newExcludedScopes = shouldExcludeScope
      ? addScope(currentExcludedScopes, currentScope)
      : removeContainedScopes(currentExcludedScopes, currentScope)
    const newExclusions = getSelectAllExclusions(value()).filter(
      (option) => !(itemMatchesScope?.(option, currentScope) ?? false),
    )
    const newInclusions = getSelectAllInclusions(value()).filter(
      (option) => !(itemMatchesScope?.(option, currentScope) ?? false),
    )

    if (
      !isGlobalSelectAll &&
      newScopes.length === 0 &&
      !activeSelectAllSearchText.value &&
      newInclusions.length === 0
    ) {
      emitNormalValue([])
      return
    }

    emitSelectAll(
      newExclusions,
      newScopes.length > 0 ? null : activeSelectAllSearchText.value,
      newInclusions,
      currentExcludedSearchTexts,
      newScopes,
      newExcludedScopes,
    )
  }

  /**
   * Handles the public Deselect All action for normal, searched, and scoped states.
   */
  function deselectAllFiltered() {
    const currentScope = getCurrentScope?.() ?? null
    const normalizedSearchText = searchText.value.trim()

    if (isSelectAllMode.value && currentScope) {
      deselectAllCurrentScope(currentScope)
      return
    }

    if (isSelectAllMode.value && getCurrentScope && !currentScope) {
      // A scope-aware field with no active scope is at the root; deselect all
      // should clear the whole sentinel rather than create a root exclusion.
      emitNormalValue([])
      return
    }

    if (normalizedSearchText) {
      if (isSelectAllMode.value) {
        if (searchTextsMatch(activeSelectAllSearchText.value, normalizedSearchText)) {
          emitSelectAllValue(getSelectAllInclusions(value()))
          return
        }

        if (!activeSelectAllSearchText.value) {
          const currentExcludedSearchTexts = getSelectAllExcludedSearchTexts(value())
          const hasExcludedSearchText = currentExcludedSearchTexts.some((excludedSearchText) =>
            searchTextsMatch(excludedSearchText, normalizedSearchText),
          )
          const newExcludedSearchTexts = hasExcludedSearchText
            ? currentExcludedSearchTexts
            : [...currentExcludedSearchTexts, normalizedSearchText]
          const newInclusions = getSelectAllInclusions(value()).filter(
            (option) => !itemMatchesSearchText(option, normalizedSearchText),
          )

          emitSelectAll(
            getSelectAllExclusions(value()),
            null,
            newInclusions,
            newExcludedSearchTexts,
          )
          return
        }

        const currentExclusions = getSelectAllExclusions(value())
        const excludedSet = new Set<SelectAllKey>(
          currentExclusions.map((option) => option.value as SelectAllKey),
        )
        const newExclusions = [...currentExclusions]
        for (const item of visibleItems()) {
          const itemValue = getItemValue(item)
          if (!excludedSet.has(itemValue)) {
            newExclusions.push(itemToOption(item))
          }
        }

        emitSelectAll(
          newExclusions,
          activeSelectAllSearchText.value,
          getSelectAllInclusions(value()),
          getSelectAllExcludedSearchTexts(value()),
        )
      } else {
        deselectVisibleItems()
      }
      return
    }

    if (normalDeselectAllMode === 'visible-items') {
      deselectVisibleItems()
      return
    }

    selectedValues.value = []
    emitNormalValue([])
  }

  /**
   * Removes visible items from the normal-mode selected key list.
   */
  function deselectVisibleItems() {
    const visibleValues = new Set(visibleItems().map((item) => getItemValue(item)))
    selectedValues.value = selectedValues.value.filter((itemValue) => !visibleValues.has(itemValue))
    emitSelectedOptions()
  }

  /**
   * Reports whether all visible items are currently selected.
   */
  function areAllFilteredSelected() {
    const items = visibleItems()
    if (items.length === 0) return false

    if (isSelectAllMode.value && requireSearchScopeCoverage) {
      return (
        currentSearchIsCoveredBySelectAllScope() &&
        !hasExcludedItemsInCurrentSearchScope() &&
        !hasExcludedSearchTextInCurrentSearchScope() &&
        items.every((item) => isItemSelected(item))
      )
    }

    return items.every((item) => isItemSelected(item))
  }

  return {
    isSelectAllMode,
    activeSelectAllSearchText,
    activeSelectAllScopes,
    activeSelectAllExcludedScopes,
    excludedValues,
    includedValues,
    excludedSearchTexts,
    itemMatchesSelectAllScope,
    itemMatchesExcludedScope,
    isItemSelected,
    includeItem,
    toggleItem,
    selectAllFiltered,
    deselectAllFiltered,
    areAllFilteredSelected,
    getOptionsFromSelectedValues,
    mapSelectedValues,
    selectedValuesChanged,
  }
}
